<template>
  <div class="timeline-module" :class="layout.lineClass" :style="timelineStyle">
    <v-progress-linear v-if="loading && !items.length" indeterminate color="primary" />
    <div v-if="!loading && !items.length" class="text-center grey--text pa-6">{{ display.emptyText || 'Aucun événement' }}</div>

    <template v-if="isTableMode && items.length">
      <v-alert v-if="!tableColumns.length" dense text type="info">Configurez les colonnes du tableau dans l’administration du module.</v-alert>
      <v-simple-table v-else class="app-theme-table timeline-module__table">
        <template #default>
          <thead>
            <tr>
              <th v-for="(column, columnIndex) in tableColumns" :key="columnIndex" scope="col" :style="tableColumnStyle(column)">{{ column.title || column.key }}</th>
              <th v-if="hasTableActions" scope="col" class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(event, index) in items" :key="eventKey(event, index)">
              <td v-for="(column, columnIndex) in tableColumns" :key="columnIndex" :style="tableColumnStyle(column)">
                <VisualRenderer :item="column" :line="displayItems[index]" :page="page" />
              </td>
              <td v-if="hasTableActions" class="text-end">
                <span v-for="action in event.actions || []" :key="action.id" class="chat-share-host d-inline-flex align-center">
                  <v-btn icon small :title="action.name" @click.stop="runAction(event, action)"><v-icon small>{{ action.icon || 'mdi-play' }}</v-icon></v-btn>
                  <rights-indicator :checks="actionInspectionChecks(action)" :label="action.name" />
                  <ChatShareMenu :modal-key="modalKey" :reference="timelineActionReference(event, action)" label="Partager cette action dans le chat" />
                </span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>

    <v-timeline
      v-if="!isTableMode && items.length"
      :dense="isSingleSide || !!layout.dense"
      :reverse="layout.side === 'left'"
      :align-top="layout.alignTop !== false"
    >
      <v-timeline-item
        v-for="(event, index) in items"
        :key="eventKey(event, index)"
        :class="[layout.itemClass, 'timeline-module__item']"
        :color="bubbleColor(event)"
        fill-dot
        large
      >
        <template #icon>
          <v-tooltip
            :disabled="!tooltip.enabled || !slotVisual(event, 'tooltip').length"
            :min-width="tooltip.minWidth || 320"
            :right="tooltip.position === 'right'"
            :left="tooltip.position === 'left'"
            :top="tooltip.position === 'top'"
            :bottom="tooltip.position === 'bottom'"
          >
            <template #activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="timeline-module__bubble" :class="layout.bubbleClass" :style="bubbleStyle(event)">
                <template v-if="slotVisual(event, 'middle').length">
                  <VisualRenderer v-for="(visual, visualIndex) in slotVisual(event, 'middle')" :key="visualIndex" :item="visual" :line="displayItems[index]" :page="page" />
                </template>
                <span v-else class="timeline-module__bubble-fallback">{{ middleFallback(event) }}</span>
              </div>
            </template>
            <div class="timeline-module__tooltip">
              <VisualRenderer v-for="(visual, visualIndex) in slotVisual(event, 'tooltip')" :key="visualIndex" :item="visual" :line="displayItems[index]" :page="page" />
            </div>
          </v-tooltip>
        </template>

        <template v-if="!isSingleSide" #opposite>
          <div class="timeline-module__opposite">
            <div v-if="layout.showDates !== false" class="text-caption grey--text mb-1">{{ formatDate(event.occurred_at) }}</div>
            <VisualRenderer v-for="(visual, visualIndex) in slotVisual(event, 'left')" :key="visualIndex" :item="visual" :line="displayItems[index]" :page="page" />
          </div>
        </template>

        <v-card :color="cardColor(event) || undefined" :class="[layout.cardClass, 'timeline-module__card']" outlined>
          <v-card-text>
            <template v-if="isSingleSide">
              <div v-if="layout.showDates !== false" class="text-caption grey--text mb-1">{{ formatDate(event.occurred_at) }}</div>
              <VisualRenderer v-for="(visual, visualIndex) in slotVisual(event, 'left')" :key="`left-${visualIndex}`" :item="visual" :line="displayItems[index]" :page="page" />
              <v-divider v-if="slotVisual(event, 'left').length && slotVisual(event, 'right').length" class="my-2" />
            </template>
            <VisualRenderer v-for="(visual, visualIndex) in slotVisual(event, 'right')" :key="`right-${visualIndex}`" :item="visual" :line="displayItems[index]" :page="page" />
          </v-card-text>
          <v-card-actions v-if="event.actions && event.actions.length" class="justify-end py-1">
            <span v-for="action in event.actions" :key="action.id" class="chat-share-host d-inline-flex align-center">
            <v-btn icon small :title="action.name" @click.stop="runAction(event, action)">
              <v-icon small>{{ action.icon || 'mdi-play' }}</v-icon>
            </v-btn>
            <rights-indicator :checks="actionInspectionChecks(action)" :label="action.name" />
            <ChatShareMenu :modal-key="modalKey" :reference="timelineActionReference(event, action)" label="Partager cette action dans le chat" />
            </span>
          </v-card-actions>
        </v-card>
      </v-timeline-item>
    </v-timeline>

    <div v-if="hasMore" class="text-center py-3">
      <v-btn small outlined color="primary" :loading="loading" @click="load(true)">
        <v-icon left small>mdi-history</v-icon>{{ pagination.loadMoreLabel || 'Charger plus' }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { client } from '../mixins'
import VisualRenderer from '@/components/engine/outils/constructVisuel/display/Renderer.vue'
import visuelConditions from '@/mixins/visuelConditions'
import { timelineDisplayLine, timelineMiddleFallback } from '@/services/timelineDisplayConversions'

export default {
  name: 'TimelineClient',
  mixins: [client, visuelConditions],
  components: { VisualRenderer },
  data: () => ({
    items: [], loading: false, nextCursor: null, hasMore: false, querys: {}, itemCursors: {},
    fromThisModule: false,
    display: { mode: 'timeline', columns: [], layout: {}, tooltip: {}, variants: [], emptyText: 'Aucun événement' },
    pagination: { limit: 100, order: 'desc', loadMoreLabel: 'Charger plus' },
  }),
  computed: {
    isTableMode() { return this.display.mode === 'table' },
    tableColumns() { return Array.isArray(this.display.columns) ? this.display.columns.filter(column => column && column.type === 'column') : [] },
    hasTableActions() { return this.items.some(event => Array.isArray(event.actions) && event.actions.length > 0) },
    // Les visuels reçoivent les alias formatés ; actions et conditions gardent les bruts.
    displayItems() { return this.items.map(event => timelineDisplayLine(event, this.display.conversion)) },
    layout() { return this.display.layout || {} },
    tooltip() { return this.display.tooltip || {} },
    isSingleSide() { return ['left', 'right'].includes(this.layout.side) },
    timelineStyle() { return { '--timeline-line-color': this.layout.lineColor || 'rgba(0,0,0,.18)' } },
    modalState() { return this.$store.getters.modalShowParams({ type: 'current', val: this.modalKey }) },
  },
  watch: {
    modalState: {
      deep: true,
      handler(state) {
        if (!state || state.show !== false || !this.fromThisModule) return
        this.fromThisModule = false
        this.load(false)
      },
    },
  },
  mounted() {
    const context = this.$store.getters.specificPage(this.modalKey) || {}
    this.params = { ...(context.params || {}), ...(this.customparams || {}) }
    this.querys = { ...(context.querys || this.$route.query || {}), ...(this.customquerys || {}) }
    this.load(false)
  },
  methods: {
    // La largeur et l’alignement suivent le contrat des colonnes constructVisuel.
    tableColumnStyle(column) {
      return { width: column.width || undefined, textAlign: { start: 'left', end: 'right', center: 'center' }[column.align] || 'left' }
    },
    load(append) {
      this.loading = true
      const cursor = append ? this.nextCursor : null
      return this.API({ type: 'post', url: 'displaymodule', params: { id: this.idmodule, params: this.params, querys: this.querys, operation: 'load', cursor } })
        .then(response => {
          const data = response.data || {}
          if (data.success === false) {
            if (data.message) this.$toast.error(data.message)
            return
          }
          const incoming = Array.isArray(data.items) ? data.items : []
          if(!append) this.itemCursors = {}
          incoming.forEach(event => { if(event.event_key) this.$set(this.itemCursors, event.event_key, cursor) })
          this.items = append ? this.uniqueItems([...this.items, ...incoming]) : incoming
          this.display = { ...this.display, ...(data.display || {}) }
          this.pagination = { ...this.pagination, ...(data.pagination || {}) }
          this.nextCursor = data.next_cursor || null
          this.hasMore = !!data.has_more
        }).finally(() => { this.loading = false })
    },
    uniqueItems(items) {
      const seen = new Set()
      return items.filter((event, index) => {
        const key = this.eventKey(event, index)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },
    variantFor(event) {
      const variants = Array.isArray(this.display.variants) ? this.display.variants : []
      const matching = variants.find(variant => {
        if (!variant || variant.key === 'default' || !Array.isArray(variant.conditions) || !variant.conditions.length) return false
        const results = this.visuelConditions(variant.conditions, event, this.page)
        return results.length > 0 && results.every(result => result === true)
      })
      return matching || variants.find(variant => variant.key === 'default') || {}
    },
    slotVisual(event, slot) {
      const visual = this.variantFor(event)[slot]
      return Array.isArray(visual) ? visual : []
    },
    bubbleColor(event) { return (this.variantFor(event).appearance || {}).bubbleColor || event.color || this.layout.bubbleColor || 'primary' },
    cardColor(event) { return (this.variantFor(event).appearance || {}).cardColor || this.layout.cardColor || '' },
    bubbleStyle(event) {
      const size = Math.max(24, Number(this.layout.bubbleSize) || 64)
      return { width: `${size}px`, height: `${size}px`, color: event.text_color || undefined }
    },
    middleFallback(event) {
      return timelineMiddleFallback(event, this.display.conversion)
    },
    eventKey(event, index) { return event.event_key || `${event.type || 'event'}:${event.source_id || index}:${event.occurred_at || ''}` },
    formatDate(value) { return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '' },
    timelineActionReference(event, action) {
      const reference = this.shareActionReference(event, action)
      return { ...reference, row_id: event.event_key || reference.row_id,
        context: { ...reference.context, event_key: event.event_key, cursor: this.itemCursors[event.event_key] || undefined } }
    },
    runAction(event, action) {
      if (action.target === 'modal') this.fromThisModule = true
      this.clickActions({
        args: event, action, idmodule: this.idmodule, page: this.page, modalKey: this.modalKey,
        success: data => {
          if (data.message) this.$toast.success(data.message)
          this.load(false)
        },
      })
    },
  },
}
</script>

<style scoped>
.timeline-module { min-width: 0; }
.timeline-module ::v-deep .v-timeline:before { background: var(--timeline-line-color); }
.timeline-module__bubble { display:flex; align-items:center; justify-content:center; overflow:hidden; border-radius:50%; font-weight:700; text-align:center; }
.timeline-module__bubble-fallback { padding:4px; line-height:1.05; overflow-wrap:anywhere; }
.timeline-module__tooltip { padding:8px; }
.timeline-module__card { min-width:0; overflow:hidden; }
.timeline-module__opposite { min-width:0; }
.timeline-module__table { width:100%; background-color:var(--app-table, #fff) !important; }
.timeline-module__table ::v-deep thead th { background-color:var(--app-table-header, #f1f3f8) !important; }
.timeline-module__table ::v-deep table { table-layout:fixed; min-width:720px; }
.timeline-module__table ::v-deep td { vertical-align:top; padding-top:10px; padding-bottom:10px; overflow-wrap:anywhere; }
@media (max-width: 600px) { .timeline-module ::v-deep .v-timeline { padding-top: 8px; } }
</style>
